import { useEffect, useRef } from "react";

import { ANIMATION_DURATION_IN_MS } from "@config/constants";

// HeadlessUI restores focus to the element that opened the dialog in a microtask after unmount; drop it.
export function useBlurOnDialogClose(isOpen: boolean) {
  const wasOpen = useRef(false);

  useEffect(() => {
    const justClosed = wasOpen.current && !isOpen;
    wasOpen.current = isOpen;
    if (!justClosed) return;

    const onFocusIn = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement) event.target.blur();
    };
    // User-initiated focus is always preceded by an input event, the restore is a bare .focus().
    const disarm = () => {
      document.removeEventListener("focusin", onFocusIn, { capture: true });
      document.removeEventListener("pointerdown", disarm, { capture: true });
      document.removeEventListener("keydown", disarm, { capture: true });
    };

    document.addEventListener("focusin", onFocusIn, { capture: true, once: true });
    document.addEventListener("pointerdown", disarm, { capture: true, once: true });
    document.addEventListener("keydown", disarm, { capture: true, once: true });

    const timeoutId = window.setTimeout(disarm, ANIMATION_DURATION_IN_MS * 2);

    return () => {
      window.clearTimeout(timeoutId);
      disarm();
    };
  }, [isOpen]);
}
